import React, {useEffect, useState} from 'react';
import authApi from "../../../../auth.api";
import {GET_VOICES} from "../../../../route";
import YoutubeIcons from "../../../../YoutubeIcon";
import {Clock, Video} from "lucide-react";
import ContentTypeDropdown from "../../create-series/ContentTypeDropDown";
import PromptExamples from "../../create-series/PromptExamples";
import LanguageDropdown from "../../create-series/LanguageDropdown";
import ArtStyleSelector from "../../../../ArtStylesSelector";
import VoiceDropdown from "../../create-series/VoiceDropdown";



const SeriesEditModal = ({
                             series,
                             onSave,
                             onClose,
                             languages,
                             themes
                         }) => {
    const [voices, setVoices] = useState([]);
    const fetchVoices = async () => {
        const { data } = await authApi.get(GET_VOICES);
        setVoices(data.voices);
    };

    useEffect(() => {
        fetchVoices();
    }, []);

    const [editedSeries, setEditedSeries] = useState(series);
    const [publishTime, setPublishTime] = useState({
        hour: series.publishTime ? new Date(series.publishTime).getHours() : new Date().getHours(),
        minute: series.publishTime ? new Date(series.publishTime).getMinutes() : new Date().getMinutes()
    });

    const platforms = [
        { name: 'YouTube Shorts', icon: YoutubeIcons },
        { name: 'TikTok', icon: Video },
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditedSeries(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePlatformToggle = (platformName) => {
        setEditedSeries(prev => ({
            ...prev,
            platforms: prev.platforms.includes(platformName)
                ? prev.platforms.filter(p => p !== platformName)
                : [...prev.platforms, platformName]
        }));
    };

    const handleOptionSelect = (field, value) => {
        setEditedSeries(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handlePublishTimeChange = (e) => {
        const [hours, minutes] = e.target.value.split(':');
        setPublishTime({
            hour: parseInt(hours),
            minute: parseInt(minutes)
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="max-w-4xl mx-auto p-6 space-y-8">
                    {/* Header */}
                    <div className="flex justify-between items-center">
                        <h2 className="text-2xl font-semibold">Edit Series</h2>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            ✕
                        </button>
                    </div>

                    {/* Platforms */}
                    <div>
                        <h5 className="text-lg font-medium mb-3 text-gray-700">
                            Platforms
                        </h5>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {platforms.map((platform) => (
                                <button
                                    key={platform.name}
                                    type="button"
                                    onClick={() => handlePlatformToggle(platform.name)}
                                    className={`
                    flex items-center p-4 rounded-lg 
                    ${editedSeries.platforms.includes(platform.name)
                                        ? 'bg-indigo-100 text-indigo-700'
                                        : 'bg-gray-100 text-gray-700'
                                    } 
                    hover:bg-indigo-200 transition-colors duration-300
                  `}
                                >
                                    <platform.icon
                                        size={24}
                                        className="w-5 h-5 mr-2"
                                        color={
                                            platform.name === 'YouTube Shorts'
                                                ? editedSeries.platforms.includes(platform.name)
                                                    ? "#FF0000"
                                                    : "#A0AEC0"
                                                : "#A0AEC0"
                                        }
                                    />
                                    {platform.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Content Type and Language */}
                    <div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Content Type */}
                            <div>
                                <h5 className="text-lg font-medium mb-3 text-gray-700">
                                    Content Type
                                </h5>
                                <ContentTypeDropdown
                                    seriesData={editedSeries}
                                    themes={themes}
                                    onSelectOption={(option) => handleOptionSelect('contentType', option)}
                                />
                                {editedSeries.contentType === 'Custom Topic' && (
                                    <>
                                        <input
                                            type="text"
                                            name="customTopic"
                                            value={editedSeries.customTopic}
                                            onChange={handleChange}
                                            className="mt-3 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                            placeholder="Enter your custom topic"
                                        />
                                        <PromptExamples />
                                    </>
                                )}
                            </div>

                            {/* Language */}
                            <div>
                                <h5 className="text-lg font-medium mb-3 text-gray-700">
                                    Language
                                </h5>
                                <LanguageDropdown
                                    seriesData={editedSeries}
                                    languages={languages}
                                    onSelectOption={(option) => handleOptionSelect('language', option)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Art Style */}
                    <ArtStyleSelector
                        selectedStyle={editedSeries.artStyle}
                        onSelectStyle={(style) => handleOptionSelect('artStyle', style)}
                    />

                    {/* Voice and Duration */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Voice */}
                        <div>
                            <h5 className="text-lg font-medium mb-3 text-gray-700">
                                Voice
                            </h5>
                            <VoiceDropdown
                                seriesData={editedSeries}
                                voices={voices}
                                onSelectOption={(option) => handleOptionSelect('voice', option)}
                            />
                        </div>

                        {/* Video Duration */}
                        <div>
                            <h5 className="text-lg font-medium mb-3 text-gray-700">
                                Video Duration
                            </h5>
                            <div className="grid grid-cols-2 gap-4">
                                {[
                                    { display: '30-60', value: 1 },
                                ].map((durationOption) => (
                                    <button
                                        key={durationOption.display}
                                        type="button"
                                        onClick={() => handleOptionSelect('duration', durationOption.value)}
                                        className={`
                      flex items-center p-4 rounded-lg 
                      ${editedSeries.duration === durationOption.value
                                            ? 'bg-indigo-100 text-indigo-700'
                                            : 'bg-gray-100 text-gray-700'
                                        } 
                      hover:bg-indigo-200 transition-colors duration-300
                    `}
                                    >
                                        <Clock className="w-5 h-5 mr-2" />
                                        {durationOption.display} seconds
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Publish Time */}
                    <div>
                        <h5 className="text-lg font-medium mb-3 text-gray-700">
                            Publish Time
                        </h5>
                        <div className="flex items-center">
                            <Clock className="w-5 h-5 mr-2 text-gray-500" />
                            <input
                                type="time"
                                value={`${publishTime.hour.toString().padStart(2, '0')}:${publishTime.minute.toString().padStart(2, '0')}`}
                                onChange={handlePublishTimeChange}
                                className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                        </div>
                    </div>


                    {/* Save/Cancel Buttons */}
                    <div className="flex justify-end gap-4 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => onSave(editedSeries)}
                            className="px-6 py-3 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default SeriesEditModal;
